import { iconSizes } from '@mds/tokens';
import { Components, stepIconClasses, stepLabelClasses, stepperClasses } from '@mui/material';
import { CSSProperties } from 'react';
import { themeVars } from '../themeVars';
import { typographyStyles, type Theme } from './utils';

const overlay: CSSProperties = {
  content: '""',
  position: 'absolute',
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
  left: 0,
  bottom: 0,
};

// Progress line pseudo element (foreground)
const progressLineSelector = `.${stepLabelClasses.root}:after`;

// Size of the step icon circle
const stepIconSize = iconSizes.lg;
const stepIconTextSize = stepIconSize * 0.8;
const stepErrorIconSize = iconSizes['2xl'];

// Spacing between the progress line and steps
const progressLineSpacing = 16;

export const MuiStepper: Components<Theme>['MuiStepper'] = {
  styleOverrides: {
    root: {
      alignItems: 'flex-start',
    },
  },
};

export const MuiStep: Components<Theme>['MuiStep'] = {
  styleOverrides: {
    root: {
      padding: 0,
    },

    completed: ({ theme }) => ({
      // Non-linear steps should not have a width / height transition on the progress line
      [`.${stepperClasses.root}:not(.${stepperClasses.nonLinear}) &`]: {
        [progressLineSelector]: {
          '--mds-Stepper-progressLineSize': '1',
          // Can be overridden in case of an error
          '--mds-Stepper-progressLineColor': themeVars(theme).palette.border.focused,
        },
      },
    }),
  },

  variants: [
    {
      props: ({ ownerState }) => !!ownerState.active,
      style: ({ theme }) => ({
        [progressLineSelector]: {
          '--mds-Stepper-progressLineSize': '1',
          // Can be overridden in case of an error
          '--mds-Stepper-progressLineColor': themeVars(theme).palette.border.focused,
        },
      }),
    },
  ],
};

export const MuiStepLabel: Components<Theme>['MuiStepLabel'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      position: 'relative',

      [`&.${stepLabelClasses.error}`]: {
        [`&${progressLineSelector}`]: {
          '--mds-Stepper-progressLineSize': '1',
          '--mds-Stepper-progressLineColor': themeVars(theme).palette.error.main,
        },

        [`.${stepLabelClasses.labelContainer}`]: {
          color: themeVars(theme).palette.text.error,
        },
      },

      // Progress line background (grey)
      [`&:before`]: {
        ...overlay,
        borderColor: themeVars(theme).palette.border.subtle,
      },

      // Progress line (completed, active, error)
      [`&:after`]: {
        ...overlay,
        borderColor: 'var(--mds-Stepper-progressLineColor, transparent)',
        transition: theme.transitions.create(['transform'], {
          duration: theme.transitions.duration.complex,
        }),
        transformOrigin: 'top left',
        [`.${stepperClasses.nonLinear} &`]: {
          transition: theme.transitions.create(['border-color'], {
            duration: theme.transitions.duration.short,
          }),
        },
      },
    }),

    horizontal: ({ theme }) => ({
      paddingRight: theme.spacing(2),
      flex: 1,

      // Progress line background (grey)
      [`&:before`]: {
        borderBlockStartWidth: 4,
        borderBlockStartStyle: 'solid',
      },

      // Progress line (completed, active, error)
      [`&:after`]: {
        transform: `scaleX(var(--mds-Stepper-progressLineSize, 0))`,
        borderBlockStartWidth: 4,
        borderBlockStartStyle: 'solid',
      },

      [`& .${stepLabelClasses.labelContainer}`]: {
        marginTop: progressLineSpacing,
      },

      [`& .${stepLabelClasses.iconContainer}`]: {
        marginTop: progressLineSpacing + 2,
        [`&.${stepLabelClasses.error}`]: {
          marginTop: progressLineSpacing,
        },
      },
    }),

    vertical: ({ theme }) => ({
      paddingLeft: theme.spacing(1.5),
      paddingBlock: theme.spacing(2.5),

      // Progress line background (grey)
      [`&:before`]: {
        borderInlineStartWidth: 4,
        borderInlineStartStyle: 'solid',
      },

      // Progress line (completed, active, error)
      [`&:after`]: {
        transform: `scaleY(var(--mds-Stepper-progressLineSize, 0))`,
        borderInlineStartWidth: 4,
        borderInlineStartStyle: 'solid',
      },

      [`& .${stepLabelClasses.iconContainer}`]: {
        marginTop: 2,
        [`&.${stepLabelClasses.error}`]: {
          marginTop: 0,
        },
      },
    }),

    labelContainer: ({ theme }) => ({
      ...typographyStyles(theme.typography.caption),
      color: themeVars(theme).palette.text.secondary,
      textAlign: 'left',
      marginLeft: 0,
    }),

    label: ({ theme }) => ({
      ...typographyStyles(theme.typography.label1),
      marginBottom: 2,
      color: themeVars(theme).palette.text.primary,

      [`&.${stepLabelClasses.completed}, &.${stepLabelClasses.active}`]: {
        fontWeight: theme.typography.fontWeightRegular,
      },

      [`&.${stepLabelClasses.disabled}`]: {
        color: themeVars(theme).palette.text.primary,
      },

      [`&.${stepLabelClasses.error}`]: {
        color: themeVars(theme).palette.text.error,
      },
    }),

    iconContainer: ({ theme }) => ({
      position: 'relative',

      // Use a white background for completed icon checkmark
      [`&.${stepLabelClasses.completed}:before`]: {
        content: '""',
        display: 'block',
        position: 'absolute',
        width: stepIconSize - 5,
        height: stepIconSize - 5,
        borderRadius: '50%',
        backgroundColor: themeVars(theme).palette.common.white,
        top: '50%',
        left: 'calc(50% - 4px)',
        transform: 'translate(-50%, -50%)',
      },

      [`&.${stepLabelClasses.error}`]: {
        color: themeVars(theme).palette.text.error,

        // A custom error icon is returned directly by MUI's StepIcon and therefore
        // does not receive the MuiStepIcon root dimensions. WarningCircle needs the
        // larger token because its artwork is inset within its viewBox.
        '& > svg': {
          width: stepErrorIconSize,
          height: stepErrorIconSize,
        },
      },
    }),
  },
};

export const MuiStepButton: Components<Theme>['MuiStepButton'] = {
  styleOverrides: {
    root: {
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
    },
  },
};

export const MuiStepIcon: Components<Theme>['MuiStepIcon'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      width: stepIconSize,
      height: stepIconSize,

      [`&:not(.${stepIconClasses.completed})`]: {
        border: `1.5px solid ${themeVars(theme).palette.border.strong}`,
        borderRadius: '50%',
        fill: 'transparent',

        [`&.${stepIconClasses.active}`]: {
          borderColor: themeVars(theme).palette.border.focused,
        },
      },

      [`&.${stepIconClasses.completed}`]: {
        position: 'relative',
      },
    }),

    text: ({ theme }) => ({
      ...typographyStyles(theme.typography.label3),
      fill: themeVars(theme).palette.text.primary,
      dominantBaseline: 'central',
      fontSize: stepIconTextSize,
      transform: 'translateY(-1px)',
    }),

    active: ({ theme }) => ({
      borderColor: themeVars(theme).palette.primary.main,
    }),
  },
};
